import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  MessagesSquare,
  X,
  Bot,
  Sparkles,
  Send,
  Mail,
  Info,
  AlertCircle,
  Lightbulb,
  ExternalLink,
  Github,
  Linkedin,
  Globe,
  FileText,
} from "lucide-react";

const AIAssistant = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [messages, setMessages] = useState([
    {
      from: "bot",
      icon: <Bot size={18} className="text-gray-900 dark:text-white" />,
      text: "👋 Hey! I’m LUMI — Ashish’s AI assistant. How can I help you today?",
      time: new Date().toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      }),
    },
  ]);
  const [typing, setTyping] = useState(false);
  const [input, setInput] = useState("");
  const [error, setError] = useState(null);
  const [currentSuggestion, setCurrentSuggestion] = useState(0);
  const [canSend, setCanSend] = useState(true);

  const [status, setStatus] = useState("online");

  const chatRef = useRef(null);

  // 🔹 New: Full sentences instead of words
  const suggestions = [
    { label: "Tell me about Ashish’s recent projects." },
    { label: "What skills does Ashish specialize in?" },
    { label: "Give me an overview of Ashish’s experience." },
    { label: "Show me Ashish’s featured work." },
    { label: "How can I contact Ashish?" },
  ];

  useEffect(() => {
    const timer = setTimeout(() => setMounted(true), 300);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    setCanSend(input.trim().length > 0 && !typing);
  }, [input, typing]);

  useEffect(() => {
    if (chatRef.current)
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
  }, [messages, typing]);

  // 🔄 Rotate suggestions every 3.5 sec
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSuggestion((prev) => (prev + 1) % suggestions.length);
    }, 3500);
    return () => clearInterval(interval);
  }, [suggestions.length]);

  const typewriterEffect = (text, callback) => {
    let i = 0;
    const speed = 25;
    const typingInterval = setInterval(() => {
      if (i < text.length) {
        setMessages((prev) => {
          const updated = [...prev];
          const last = updated[updated.length - 1];
          if (last?.from === "bot" && last.partial) {
            updated[updated.length - 1] = {
              ...last,
              text: text.slice(0, i + 1),
              partial: true,
            };
          } else {
            updated.push({
              from: "bot",
              icon: <Bot size={16} className="text-sky-500" />,
              text: text.charAt(i),
              partial: true,
            });
          }
          return updated;
        });
        i++;
      } else {
        clearInterval(typingInterval);
        setMessages((prev) => {
          const updated = [...prev];
          updated[updated.length - 1] = {
            ...updated[updated.length - 1],
            text,
            partial: false,
            time: new Date().toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            }),
          };
          return updated;
        });
        callback?.();
      }
    }, speed);
  };

  const handleSend = async (text) => {
    if (!text.trim() || !canSend) return;

    setCanSend(false);
    setError(null);

    // USER MESSAGE
    setMessages((prev) => [
      ...prev,
      {
        from: "user",
        text,
        time: new Date().toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        }),
      },
    ]);

    setInput("");

    setStatus("thinking");
    setTyping(true);

    let reply;

    try {
      const res = await fetch(
        "https://backend-five-ochre-85.vercel.app/api/chat",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ message: text }),
        }
      );

      if (!res.ok) throw new Error("Server error");

      const data = await res.json();
      reply = data.reply || "Sorry, I couldn’t get that.";

      setStatus("online");
    } catch (error) {
      console.error(error);
      reply = "⚠️ Unable to connect right now.";
      setStatus("offline");
    }

    // 🔥 Add placeholder bot message BEFORE typewriter starts
    setMessages((prev) => [
      ...prev,
      {
        from: "bot",
        icon: <Bot size={16} className="text-sky-500" />,
        text: "",
        partial: true,
      },
    ]);

    // 🔥 Smooth animation typing starts
    typewriterEffect(reply, () => {
      // When finished typing, finalize the message
      setMessages((prev) => {
        const updated = [...prev];
        const last = updated[updated.length - 1];

        updated[updated.length - 1] = {
          ...last,
          text: reply,
          partial: false,
          time: new Date().toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          }),
        };

        return updated;
      });

      setTyping(false);
      setCanSend(true);
    });
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && canSend) handleSend(input);
  };

  // 🌐 Smart link renderer
  const LinkRenderer = ({ href, children }) => {
    if (!href) return <span>{children}</span>;
    const domain = new URL(href, window.location.origin).hostname.replace(
      "www.",
      ""
    );

    const icon = domain.includes("github") ? (
      <Github size={14} />
    ) : domain.includes("linkedin") ? (
      <Linkedin size={14} />
    ) : domain.includes("mailto") ? (
      <Mail size={14} />
    ) : domain.includes("docs") ? (
      <FileText size={14} />
    ) : (
      <Globe size={14} />
    );

    return (
      <motion.a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.97 }}
        className="inline-flex items-center gap-2 px-2.5 py-1.5 mt-1 mr-1 
                   rounded-lg bg-gray-200/60 dark:bg-white/15
text-gray-900 dark:text-white
border border-gray-400/30 dark:border-white/20
hover:bg-gray-300/60 dark:hover:bg-white/25
 transition-all duration-200 cursor-pointer"
      >
        {icon}
        <span className="truncate max-w-[120px]">{children}</span>
        <ExternalLink size={12} className="opacity-70" />
      </motion.a>
    );
  };

  if (!mounted) return null;

  return (
    <>
      {/* Floating Chat Button */}
      <div className="fixed bottom-6 right-6 z-[9999] group">
        {/* Tooltip */}
        <div
          className="
      absolute bottom-16 right-0
      px-3.5 py-1.5
      rounded-lg text-[11px]
      font-medium tracking-wide
      select-none whitespace-nowrap

      bg-white/85 dark:bg-gray-900/80
      backdrop-blur-xl
      border border-gray-200/60 dark:border-white/10
      shadow-[0_4px_12px_rgba(0,0,0,0.15)]

      opacity-0 group-hover:opacity-100
      group-hover:translate-y-0
      translate-y-1
      transition-all duration-200
      pointer-events-none

      text-gray-700 dark:text-gray-300
    "
        >
          Ask lumi any thing!
          <div
            className="
        absolute left-1/2 -bottom-1.5
        -translate-x-1/2
        w-2.5 h-2.5 rotate-45
        bg-white/85 dark:bg-gray-900/80
        border-b border-r 
        border-gray-200/60 dark:border-white/10
      "
          />
        </div>

        {/* Floating Button */}
        <motion.button
          onClick={() => setIsOpen(!isOpen)}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          animate={{
            y: [0, -5, 0],
            transition: { duration: 2.5, repeat: Infinity },
          }}
          className="
      p-4 rounded-full 
bg-gray-900 dark:bg-white 
text-white dark:text-gray-900
shadow-[0_4px_10px_rgba(0,0,0,0.15)]
hover:shadow-[0_8px_20px_rgba(0,0,0,0.35)]
transition-all duration-300

    "
        >
          {isOpen ? <X size={22} /> : <MessagesSquare size={26} />}
        </motion.button>
      </div>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.9 }}
            transition={{ duration: 0.3 }}
            className="fixed bottom-24 right-4 sm:right-6 w-[92vw] sm:w-80 md:w-96 
                       max-h-[80vh] flex flex-col rounded-2xl overflow-hidden 
                       backdrop-blur-2xl border border-white/20 
                       bg-white/70 dark:bg-gray-900/70 shadow-2xl z-[9999]"
          >
            <div
              className="flex items-center justify-between px-4 py-3 
              bg-gray-900 dark:bg-white
text-white dark:text-gray-900
 text-white shadow-md"
            >
              <div className="flex items-center gap-3">
                <motion.div
                  animate={{ rotate: [0, 15, -15, 0] }}
                  transition={{ duration: 2, repeat: Infinity, repeatDelay: 6 }}
                  className="p-0.5 rounded-full bg-white/20 border border-white/30 shadow-sm"
                >
                  {/* Small Avatar */}
                  <img
                    src="/avatar.png"
                    alt="avatar"
                    className="w-6 h-6 sm:w-7 sm:h-7 rounded-full object-cover"
                  />
                </motion.div>

                <div className="flex flex-col leading-tight">
                  <h3 className="font-semibold text-sm sm:text-base flex items-center gap-1">
                    Ashish’s AI Assistant
                    <Sparkles className="text-yellow-300 w-4 h-4" />
                  </h3>

                  {/* Dynamic Status */}
                  <div className="flex items-center gap-1 mt-0.5">
                    {status === "online" && (
                      <>
                        <span className="relative flex h-2 w-2">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                        </span>
                        <span className="text-[11px] text-white dark:text-black/80">
                          Online
                        </span>
                      </>
                    )}

                    {status === "thinking" && (
                      <>
                        <span className="h-2 w-2 bg-blue-300 rounded-full animate-pulse"></span>
                        <span className="text-[11px] text-white dark:text-black/80">
                          Thinking…
                        </span>
                      </>
                    )}

                    {status === "offline" && (
                      <>
                        <span className="h-2 w-2 bg-red-400 rounded-full animate-pulse"></span>
                        <span className="text-[11px] text-white dark:text-black/80">
                          Offline
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </div>

              <button
                onClick={() => setIsOpen(false)}
                className="text-white dark:text-black/90 hover:text-white"
              >
                <X size={18} />
              </button>
            </div>

            {/* Chat Body */}
            <div
              ref={chatRef}
              className="flex-1 overflow-y-auto p-4 space-y-3 text-sm 
                         scrollbar-thin scrollbar-thumb-sky-300/40 dark:scrollbar-thumb-gray-700/50"
            >
              {messages.map((msg, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.25 }}
                  className={`flex items-end gap-2 ${
                    msg.from === "user" ? "justify-end" : "justify-start"
                  }`}
                >
                  {/* BOT ICON (if any) */}
                  {msg.from === "bot" && (
                    <div className="flex-shrink-0 mt-auto">
                      <img
                        src="/avatar.png"
                        alt="bot avatar"
                        className="w-7 h-7 sm:w-8 sm:h-8 rounded-full object-cover border border-gray-300 dark:border-gray-600 shadow-md"
                      />
                    </div>
                  )}

                  <div className="flex flex-col max-w-[75%]">
                    {/* MESSAGE BUBBLE */}
                    <div
                      className={`px-3 py-2 rounded-2xl leading-snug break-words 
        ${
          msg.from === "user"
            ? "bg-gray-900 dark:bg-white text-white dark:text-gray-900 shadow-md"
            : "bg-gray-50 dark:bg-gray-800/80 text-gray-900 dark:text-gray-100 border border-gray-200 dark:border-gray-700"
        }`}
                    >
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={{ a: LinkRenderer }}
                      >
                        {msg.text}
                      </ReactMarkdown>
                    </div>

                    {/* TIME BELOW BUBBLE */}
                    <span
                      className={`text-[10px] mt-1 px-1
        ${
          msg.from === "user"
            ? "text-white/70 text-right"
            : "text-gray-400 dark:text-gray-500 text-left"
        }`}
                    >
                      {msg.time}
                    </span>
                  </div>
                </motion.div>
              ))}

              {typing && (
                <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                  <img
                    src="/avatar.png"
                    alt="bot avatar"
                    className="w-6 h-6 rounded-full object-cover border border-gray-300 dark:border-gray-600 shadow-sm"
                  />

                  <div className="flex gap-1">
                    {[0, 0.2, 0.4].map((d, i) => (
                      <motion.span
                        key={i}
                        animate={{ y: [0, -4, 0] }}
                        transition={{
                          duration: 0.5,
                          repeat: Infinity,
                          delay: d,
                        }}
                        className="w-2 h-2 bg-gray-400 dark:bg-gray-500 rounded-full"
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Error Bar */}
            {error && (
              <div className="px-3 py-2 bg-red-50 dark:bg-red-900/40 text-red-700 dark:text-red-300 text-xs flex items-center gap-2 border-t border-red-200 dark:border-red-800">
                <AlertCircle size={14} /> {error}
              </div>
            )}

            {/* Input + Hover Suggestion Icon */}
            <div className="border-t border-white/10 bg-white/40 dark:bg-gray-900/50 backdrop-blur-md p-3">
              <div className="flex items-center gap-2 relative">
                {/* Input */}
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => !typing && handleKeyPress(e)}
                  disabled={typing}
                  placeholder={
                    typing
                      ? "Lumi is typing..."
                      : "Ask me about my work and experience..."
                  }
                  className={`flex-1 px-3 py-2 rounded-lg text-sm border 
              ${typing ? "opacity-60 cursor-not-allowed" : ""}
              border-gray-300 dark:border-gray-700 
              bg-white/70 dark:bg-gray-800/70 text-gray-900 dark:text-gray-100 
              placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 
              focus:ring-sky-400 focus:outline-none transition-all duration-200`}
                />

                {/* Hover Icon + Animated Popup */}
                <div className="relative group flex-shrink-0">
                  <Info
                    size={18}
                    className="text-gray-700 dark:text-gray-300 cursor-pointer"
                  />

                  <AnimatePresence>
                    <motion.div
                      initial={{ opacity: 0, scale: 0.85, y: 8 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.85, y: 8 }}
                      transition={{ duration: 0.2, ease: "easeOut" }}
                      className="absolute bottom-8 right-0 w-56 p-3 rounded-xl 
          bg-white dark:bg-gray-800 shadow-lg border border-gray-200 dark:border-gray-700 
          text-xs invisible group-hover:visible pointer-events-none"
                    >
                      <div className="font-semibold text-gray-700 dark:text-gray-200 mb-1 flex items-center gap-2">
                        <Lightbulb size={16} className="text-yellow-500" />
                        Suggestions
                      </div>

                      <motion.div
                        key={currentSuggestion}
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.25 }}
                        className="px-2 py-2 rounded-lg bg-sky-100 dark:bg-sky-900/40 
                       text-sky-700 dark:text-sky-300 border border-sky-300/40 
                       cursor-pointer hover:bg-sky-200 dark:hover:bg-sky-800/60 
                       transition pointer-events-auto"
                        onClick={() =>
                          canSend &&
                          handleSend(suggestions[currentSuggestion].label)
                        }
                      >
                        {suggestions[currentSuggestion].label}
                      </motion.div>
                    </motion.div>
                  </AnimatePresence>
                </div>

                {/* Send Button */}
                <motion.button
                  whileHover={canSend ? { scale: 1.08 } : {}}
                  whileTap={canSend ? { scale: 0.95 } : {}}
                  disabled={!canSend}
                  onClick={() => canSend && handleSend(input)}
                  className={`p-2 rounded-lg bg-gray-900 dark:bg-white text-white dark:text-gray-900
 shadow-md transition-all duration-200
    ${
      canSend
        ? "hover:shadow-lg hover:shadow-sky-400/30"
        : "opacity-50 cursor-not-allowed"
    }`}
                >
                  <Send size={16} />
                </motion.button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default AIAssistant;
